/* eslint-disable @typescript-eslint/no-explicit-any */

import React, {
  FC,
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';
import { RadioButton } from 'primereact/radiobutton';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PagesPaths } from '@/enums';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDollar } from '@fortawesome/free-solid-svg-icons';
import {
  Button,
  DropdownField,
  InputTextField,
  LoadingSpinner,
} from '@/components';
import { InputNumberValueChangeEvent } from 'primereact/inputnumber';
import { Banco } from '@/types/depo20';
import { Divider } from 'primereact/divider';
import { useToastNotification, useAuthContext } from '@/hooks';
import { titularRut } from '@/pages/CustomerRegistration/titularRut';
import { Value } from '@/types/getPendingPaymentsType';

import styles from '../Depo20/components/ModalDetails.module.scss';
import { getBancos } from '../Depo20/services/getBancos';
import { getValidarPagarDocumento } from '../Depo20/services/getValidarPagarDocumento';
import { getBuscarDocumento } from '../Depo20/services/getBuscarDocumento';
import { getDepo20TableList } from '../Depo20/services/getDepo20TableList';
import { getVisitasReliquidadas } from '../Depo20/services/getVisitasReliquidadas';
import { pagoSistarBanc } from '../Depo20/services/pagoSistarBanc';
import { pagoWeb } from '../Depo20/services/pagoWeb';
import { getTotalPreDocumento } from '../Depo20/services/getTotalPredocumento';
import { getPagoDocumentoBooking } from '../Depo20/services/pagoDocumentoBooking';
import { getPagoDocumentoLiquidacion } from '../Depo20/services/pagoDocumentoLiquidacion';
import { comprobarDocumento } from '../Depo20/services/comprobarDocumento';
import { Depo20FormState } from '../Depo20/Depo20ClientForm';
import { PendingPaymentsFiltersType } from './components/PendingPaymentsFilters';

type ModalDetailsProps = {
  oid: number;
  isDialogShown: boolean;
  toggleDialog: () => void;
  formClientData: Value | undefined;
  formDataValues?: Depo20FormState;
  getData?: any;
  filters?: PendingPaymentsFiltersType;
  accountStatementBody?: any;
};

interface DocumentTypeFormState {
  documentType: string | null;
  documentNumber: string | null;
  bank: number;
}

interface DocumentTypeFormErrors {
  documentType: string;
  documentNumber: string;
  bank: string;
}

const ModalPendingPayments: FC<ModalDetailsProps> = ({
  oid,
  isDialogShown,
  toggleDialog,
  formClientData,
  formDataValues,
  getData,
  filters,
  accountStatementBody,
}) => {
  const { showToast } = useToastNotification();
  const { userDataDecoded } = useAuthContext();

  const baseUrl = new URL(window.location.origin).origin;
  localStorage.setItem('urlVuelta', `${baseUrl}${PagesPaths.mySettlement}`);

  const paymentUrl =
    process.env['REACT_APP_PAYMENT_URL'] ??
    'https://spf.quanam.com/spfe/servlet/PagoEmpresa';

  const { t } = useTranslation('depo20');
  const paymentOption = [{ key: 'pagoOnline', value: t('payOnlineOption') }];

  const [confirmButtonPress, setConfirmButtonPress] = useState<boolean>(false);

  const [selectedPayment, setSelectedPayment] = useState<string | null>(
    'pagoOnline'
  );

  const docNumberLocalStorage =
    Number(window.localStorage.getItem(`pagoOnlineRUT`)) ??
    Number(window.localStorage.getItem(`pagoOnlineCI`)) ??
    null;

  const [formState, setFormState] = useState<DocumentTypeFormState>({
    documentType: 'RUT',
    documentNumber:
      docNumberLocalStorage !== 0 ? `${docNumberLocalStorage}` : null,
    bank: 0,
  });

  const [formErrors, setFormErrors] = useState<DocumentTypeFormErrors>({
    documentType: '',
    documentNumber: '',
    bank: '',
  });

  const { data: comboBancos } = useQuery({
    queryKey: ['comboBancoListDropdown'],
    queryFn: getBancos,
  });

  const selectedBank: Banco | undefined = comboBancos?.find(
    (val) => val.oid === formState.bank
  );

  const formRef = useRef<HTMLFormElement>(null);

  const createAndSubmitForm = (data: any) => {
    const form = formRef.current;
    if (form) {
      while (form.firstChild) {
        form.removeChild(form.firstChild);
      }

      Object.keys(data).forEach((key) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = data[key];
        form.appendChild(input);
      });

      form.submit();
    }
  };

  const {
    mutateAsync: totalDocumentoBooking,
    isPending: getTotalDocumentoBookingLoading,
  } = useMutation({
    mutationKey: ['getTotalDocumentoBooking', oid],
    mutationFn: () => getPagoDocumentoBooking(oid),
  });

  const {
    mutateAsync: totalPagoDocumentoLiquidacion,
    isPending: getTotalPagoDocumentoLiquidacionLoading,
  } = useMutation({
    mutationKey: ['getTotalPagoDocumentoLiquidacion', oid],
    mutationFn: () => getPagoDocumentoLiquidacion(oid),
  });

  const {
    mutateAsync: totalPredocumento,
    isPending: getTotalPredocumentoLoading,
  } = useMutation({
    mutationKey: ['getTotalPredocumento', oid],
    mutationFn: () => getTotalPreDocumento(oid),
  });

  const { mutate: getPagoSistarBanc, isPending: getPagosSistarBancPending } =
    useMutation({
      mutationKey: ['pagoSistarBank', oid],
      mutationFn: () =>
        pagoSistarBanc({
          oidLiquidacion: oid,
          oidBanco: selectedBank?.oid,
          documentoParaPago: Number(formState.documentNumber),
        }),

      onSuccess: async (response) => {
        if (response.ok) {
          const formData = {
            idBanco: process.env['REACT_APP_BASE_URL']?.includes('testing')
              ? '009'
              : selectedBank?.codigoBCU,
            idTransaccion: response.guidMensaje,
            idOrganismo: 'MONTECON',
            tipoServicio: 'SERV',
            idCuenta: formState.documentNumber,
            idFactura: oid,
            importe: formClientData?.totalAmount
              ? Math.round(Number(formClientData.totalAmount) * 100)
              : 0,
            moneda: 'USD',
            otroDato: formDataValues?.referencia ?? '',
            urlVuelta: `${localStorage.getItem('urlVuelta')}`,
          };

          createAndSubmitForm(formData);
        } else {
          showToast({
            severity: 'error',
            detail: t('cannotPaySettlement'),
          });
        }
      },
    });

  const { mutate: getPagoWeb, isPending: getPagosWeb } = useMutation({
    mutationKey: ['pagoWeb', oid],
    mutationFn: () =>
      pagoWeb({
        oidLiquidacion: oid,
        oidBanco: selectedBank?.oid,
        documento: `${formState.documentNumber}`,
        tipoDocumento: formState.documentType ?? 'RUT',
      }),
    onSuccess: (response) => {
      if (!response.ok) {
        showToast({
          severity: 'error',
          detail: response.mensaje || 'Hubo un error al registrar el pago',
        });
      }
      if (response.ok) {
        showToast({
          severity: 'success',
          detail: response.mensaje || 'Pago efectuado correctamente',
        });
      }
    },
    onSettled: () => {
      toggleDialog();
      if (filters) {
        getData(filters);
      }
      if (accountStatementBody) {
        getData(accountStatementBody);
      }
    },
  });

  const {
    mutateAsync: getVisitasLiquidacion,
    isPending: getVisitasLiquidacionLoading,
  } = useMutation({
    mutationKey: ['VisitasReliquidadas', oid],
    mutationFn: () => getVisitasReliquidadas(oid),
  });

  const {
    mutate: getSeleccionadosEnLiquidacion,
    isPending: getSeleccionadosEnLiquidacionLoading,
  } = useMutation({
    mutationKey: ['GetSeleccionadosBankLiqui', oid],
    mutationFn: () => getDepo20TableList(oid),
    onSuccess: async () => {
      await getVisitasLiquidacion();

      const totalAmount = formClientData?.totalAmount ?? 0;

      const showMessageErrorAmountIs0 = () =>
        showToast({
          severity: 'error',
          detail: t('cannotPaySettlement'),
        });

      if (
        selectedPayment === 'facturaWeb' ||
        selectedPayment === 'facturaUnica'
      ) {
        if (totalAmount <= 0) {
          showMessageErrorAmountIs0();
        }
      }

      if (selectedPayment === 'pagoOnline') {
        if (totalAmount <= 0) {
          showMessageErrorAmountIs0();
        }

        if (
          (formState.documentType === 'RUT' &&
            selectedBank?.intermediarioPagosElectronicos === 'BEVSA') ||
          (formState.documentType === 'CI' &&
            selectedBank?.intermediarioPagosElectronicosCI === 'BEVSA')
        ) {
          getPagoWeb();
        } else {
          getPagoSistarBanc();
        }
      }
    },
  });

  const { mutate: fetchBuscarDocumento, isPending: buscarDocLoading } =
    useMutation({
      mutationKey: ['BuscarDocumento', oid],
      mutationFn: () => getBuscarDocumento(oid),
      onSuccess: (response) => {
        if (response) {
          showToast({
            severity: 'error',
            detail: t('documentsRegisteredForSettlement'),
          });
        }
        if (formClientData?.tipo === 'DocumentoCredito') {
          getSeleccionadosEnLiquidacion();
        }
      },
    });

  const { mutate: mutateTitularRut, isPending: rutTitularLoading } =
    useMutation({
      mutationFn: titularRut,
      onSuccess: (response) => {
        if (Object.keys(response).length && selectedPayment === 'pagoOnline') {
          fetchBuscarDocumento();
        }
        if (!Object.keys(response).length) {
          showToast({
            severity: 'error',
            detail: t('titularDoesntExiste'),
          });
        }
        if (
          !userDataDecoded?.facwebcc &&
          (selectedPayment === 'facturaWeb' ||
            selectedPayment === 'facturaUnica')
        ) {
          showToast({
            severity: 'error',
            detail:
              'El RUT ingresado no está entre sus cuentas corrientes autorizadas.',
          });
        }

        if (userDataDecoded?.facwebcc && selectedPayment === 'facturaWeb') {
          const isRutAllowed = userDataDecoded?.facwebcc.includes(
            formClientData?.rutCliente ?? ''
          );

          if (!isRutAllowed) {
            showToast({
              severity: 'error',
              detail:
                'El RUT ingresado no está entre sus cuentas corrientes autorizadas.',
            });
          }
          if (isRutAllowed) {
            getSeleccionadosEnLiquidacion();
          }
        }
        if (selectedPayment === 'facturaUnica') {
          getSeleccionadosEnLiquidacion();
        }
      },
    });

  const { mutate: fetchComprobarDocumento, isPending: comprobarDocLoading } =
    useMutation({
      mutationKey: ['ComprobarDocumento', oid],
      mutationFn: () =>
        comprobarDocumento({ documento: formClientData?.rutCliente, tipo: 2 }),
      onSuccess: (response) => {
        if (response === 1) {
          showToast({
            severity: 'error',
            detail:
              'El RUT ingresado pertenece a un titular sin dirección. En el proceso de factura electrónica es necesario que el titular contenga el dato mencionado. Comuníquese con atención al cliente para regularizar la información',
          });
        }
        if (response === 2) {
          showToast({
            severity: 'error',
            detail: 'El RUT ingresado es inválido',
          });
        } else {
          mutateTitularRut({ rut: `${formClientData?.rutCliente}` });
        }
      },
    });

  const { mutate: fetchValidarDocumento, isPending: validarLoading } =
    useMutation({
      mutationKey: ['fetchValidarDocumento', formState.documentNumber],
      mutationFn: () =>
        getValidarPagarDocumento(Number(formState.documentNumber)),
      onSuccess: (response) => {
        if (response === true) {
          mutateTitularRut({ rut: `${formState.documentNumber}` });
        }
        if (response === false) {
          showToast({ severity: 'error', detail: t('rutValidationFailed') });
        }
      },
    });

  const dialogHeader = (
    <div className={`${styles.header} text-blue-800`}>
      <FontAwesomeIcon
        icon={faDollar}
        style={{ marginRight: '0.5rem' }}
        className="mr-3"
      />
      {t('paymentTitle')}
    </div>
  );

  const handleClientSelectionChange = (key: string) => {
    setSelectedPayment(key);
  };

  const documentTypes = [
    { name: 'Cédula de Identidad', value: 'CI' },
    { name: 'RUT', value: 'RUT' },
  ];

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement> | InputNumberValueChangeEvent,
    name: keyof DocumentTypeFormState
  ) => {
    const { value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
  };

  const validateForm = () => {
    let errors: DocumentTypeFormErrors = { ...formErrors };

    if (!formState.documentNumber) {
      errors = {
        ...errors,
        documentNumber: t('documentValidation'),
      };
    }

    setFormErrors(errors);
    return Object.values(errors).every((x) => x === '');
  };

  const handleDropdownChange = (
    e: { value: string | null },
    name: keyof DocumentTypeFormState
  ) => {
    setFormState((prevState) => ({
      ...prevState,
      [name]: e.value,
    }));
  };
  const validateRut = useCallback((): void => {
    const errors = { ...formErrors };

    if (formState.documentType === 'RUT') {
      if (!formState.documentNumber) {
        errors.documentNumber = t('paymentModalRutRequired');
      } else if (!/^\d+$/.test(formState.documentNumber)) {
        errors.documentNumber = t('paymentModalRutNumbers');
      } else {
        errors.documentNumber = '';
      }
    } else if (formState.documentType === 'CI') {
      if (!formState.documentNumber) {
        errors.documentNumber = t('ciRequired');
      } else if (!/^\d{7,8}$/.test(formState.documentNumber)) {
        errors.documentNumber = t('ciNumbers');
      } else {
        errors.documentNumber = '';
      }
    }

    setFormErrors(errors);
  }, [formErrors, formState.documentType, formState.documentNumber, t]);

  useEffect(() => {
    validateRut();
  }, [formState.documentNumber, formState.documentType, validateRut]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    validateForm();
    validateRut();

    if (
      selectedPayment === 'facturaWeb' ||
      (selectedPayment === 'facturaUnica' && formClientData)
    ) {
      fetchComprobarDocumento();
    }

    if (validateForm()) {
      if (formState.documentType === 'CI') {
        localStorage.setItem('pagoOnlineCI', `${formState.documentNumber}`);
      }
      if (!formState.documentType || formState.documentType === 'RUT') {
        localStorage.setItem('pagoOnlineRUT', `${formState.documentNumber}`);
      }

      if (
        selectedPayment === 'pagoOnline' &&
        (formState.documentType === 'RUT' || !formState.documentType)
      ) {
        fetchValidarDocumento();
      }

      if (selectedPayment === 'pagoOnline' && formState.documentType === 'CI') {
        fetchBuscarDocumento();
      }
      if (
        formClientData?.tipo === 'DEPO20' ||
        formClientData?.tipo === 'Proforma'
      ) {
        totalPredocumento();
      }

      if (formClientData?.tipo === 'DocumentoCredito') {
        totalPagoDocumentoLiquidacion();
      }

      if (formClientData?.tipo === 'VacíoExportación') {
        totalDocumentoBooking();
      }
    }
  };

  const validateShowDocumentAndTypeOrRut =
    (selectedBank?.intermediarioPagosElectronicos === 'BEVSA' ||
      selectedBank?.intermediarioPagosElectronicosCI === 'BEVSA') &&
    selectedBank.permitePagarConCI === true;

  const validateFormData =
    formState.documentType === 'RUT'
      ? 'onlyRutPaymentPlaceholder'
      : 'documentLabel';
  const documentPlaceholder = !validateShowDocumentAndTypeOrRut
    ? 'onlyRutPaymentPlaceholderNew'
    : validateFormData;

  const validateFormDataName =
    formState.documentType === 'RUT'
      ? 'documentPaymentModalInputField'
      : 'documentLabel';
  const documentLabel = !validateShowDocumentAndTypeOrRut
    ? 'documentPaymentModalInputField'
    : validateFormDataName;

  return (
    <Dialog
      header={dialogHeader}
      visible={isDialogShown}
      style={{ width: window.innerWidth <= 500 ? '95vw' : '480px' }}
      onHide={toggleDialog}
      modal
    >
      <form
        ref={formRef}
        action={paymentUrl}
        method="POST"
        style={{ opacity: 0, height: 0, width: 0 }}
        target="_self"
        id="PostForm"
        name="PostForm"
      />
      <div className="p-4">
        <form onSubmit={handleSubmit}>
          <ul className={`list-none p-0 m-0 ${styles.list}`}>
            <div className={styles.paymentMethod}>
              {t('paymentMethodLabel')}
            </div>
            <hr className={styles.topDivider} />

            {paymentOption.map((client) => (
              <div
                key={client.key}
                className={`${styles.clientList} ${styles.paymentModal} `}
              >
                <hr className={styles.topDivider} />

                <li className="flex justify-content-start  align-items-center">
                  <RadioButton
                    inputId={client.key}
                    name="favoriteClient"
                    value={client.key}
                    onChange={() => handleClientSelectionChange(client.key)}
                    checked={selectedPayment === client.key}
                  />
                  <label htmlFor={client.key} className="ml-4 text-dark">
                    {client.value}
                  </label>
                </li>
                <hr />
              </div>
            ))}
            {!confirmButtonPress &&
              (validarLoading ||
                rutTitularLoading ||
                buscarDocLoading ||
                getSeleccionadosEnLiquidacionLoading ||
                getVisitasLiquidacionLoading ||
                getPagosWeb ||
                getPagosSistarBancPending ||
                getTotalPredocumentoLoading ||
                getTotalPagoDocumentoLiquidacionLoading ||
                getTotalDocumentoBookingLoading ||
                comprobarDocLoading) && (
                <LoadingSpinner className="flex justify-content-center align-items-center my-5" />
              )}
          </ul>
          <Divider className={styles.paymentDivider} />

          {selectedPayment === 'pagoOnline' && (
            <>
              <DropdownField
                label={t('bankLabel')}
                options={comboBancos?.map((banco: Banco) => ({
                  name: banco.nombre,
                  value: banco.oid,
                }))}
                value={formState.bank}
                onChange={(e) => handleDropdownChange(e, 'bank')}
                placeholder={t('bankPlaceholder')}
                className="mb-4"
              />
              {validateShowDocumentAndTypeOrRut && (
                <DropdownField
                  label={t('documentTypeLabel')}
                  options={documentTypes}
                  value={formState.documentType}
                  onChange={(e) => handleDropdownChange(e, 'documentType')}
                  placeholder={t('selectDocumentTypePlaceholder')}
                  className="mb-4"
                />
              )}
              <InputTextField
                id="documentNumber"
                name="documentNumber"
                value={formState.documentNumber ?? ''}
                onChange={(e) => handleInputChange(e, 'documentNumber')}
                placeholder={t(documentPlaceholder)}
                error={formErrors.documentNumber}
                label={t(documentLabel)}
              />
              <div className="flex justify-content-end text-grey font-12">
                {t('redirectionNotice')}
              </div>
            </>
          )}
          <div className="flex justify-content-end mt-4">
            <button
              type="button"
              className={`${styles.cancelButton} mr-4`}
              onClick={toggleDialog}
            >
              {t('cancelButton')}
            </button>
            <Button
              className={`${styles.selectButton}`}
              type="submit"
              loading={
                confirmButtonPress &&
                (validarLoading ||
                  rutTitularLoading ||
                  buscarDocLoading ||
                  getSeleccionadosEnLiquidacionLoading ||
                  getVisitasLiquidacionLoading ||
                  getPagosWeb ||
                  getPagosSistarBancPending ||
                  getTotalPredocumentoLoading ||
                  getTotalPagoDocumentoLiquidacionLoading ||
                  getTotalDocumentoBookingLoading ||
                  comprobarDocLoading)
              }
              onClick={() => setConfirmButtonPress(true)}
            >
              {t('confirmButton')}
            </Button>
          </div>
        </form>
      </div>
    </Dialog>
  );
};

export default ModalPendingPayments;
